import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {connect} from 'react-redux';
import {Transition} from 'react-transition-group';
import {StyledBillsTabs, StyledBillsTabsDeleteModal} from './StyledBillsTabs';
import {
  StyledJournalEntriesTabsContainer
} from '../../NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import EditAccountingRowSection from '../../NetSuiteJournalEntriesPage/EditAccountingRowSection';
import ReloadButton from '../../NetSuiteJournalEntriesPage/ReloadButton';
import TableFiltersRightSideContent from '../../NetSuiteJournalEntriesPage/TableFiltersRightSideContent';
import Tabs from '../../../Tabs';
import BillsHistoryTab from '../tabs/BillsHistoryTab';
import ErpBillsTab from '../tabs/ErpBillsTab';
import MissingErpTab from '../tabs/MissingErpTab';
import {
  accountingHelpers,
  elementHelpers,
  fileHelpers,
  locationHelpers,
  tabHelpers,
  transactionsHelpers,
  vendorsHelpers,
} from '../../../../utils/helpers';
import {accountingActions} from '../../../../state/actions/accounting.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {vendorsActions} from '../../../../state/actions/vendors.actions';
import {useAccountingTabLogic, useTabState} from '../../../../hooks';
import {
  accountingEditVariants,
  billInvoiceSourceConstants,
  billInvoiceSyncStatusConstants,
  DEFAULT_MODAL_PROPS,
  SYSTEM_ANIMATION_DURATION
} from '../../../../constants';

const tabKeys = {
  ERP_BILLS: 'erp_bills',
  NOT_ON_ERP: 'not_on_erp',
  SYNCED: 'synced',
};

const {ERP_BILLS, NOT_ON_ERP, SYNCED} = tabKeys;

const defaultTabKey = tabKeys.NOT_ON_ERP;

const tabKeysArr = [NOT_ON_ERP, SYNCED, ERP_BILLS];

const {showAlert} = accountingHelpers;

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION, detailsMaxWidth: 510});

const BillsTabs = ({
  dispatch,
  employees,
  vendors,
  deleteInvoice,
  downloadAccountingInvoice,
  getInvoices,
  getBatchVendors,
  getInvoicesTotals,
  syncInvoices,
  updateInvoice,
  ...rest
}) => {
  const location = useLocation();
  const [t] = useTranslation(['main', 'accounting', 'invoices']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || defaultTabKey);
  const [deleteModalProps, setDeleteModalProps] = useState({...DEFAULT_MODAL_PROPS, id: null});
  const [total, setTotal] = useState(undefined);
  const [detailsWindowProps, setDetailsWindowProps] = useState({open: false, item: null, variant: accountingEditVariants.VENDOR_MISSING});
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
    requiredUpdate, setRequiredUpdate,
    selectedRowKeys, setSelectedRowKeys,
  } = useTabState({tabKeys: tabKeysArr});

  const loadTotals = () => {
    getInvoicesTotals({
      successCallback: (data) => {
        setTotal({
          [NOT_ON_ERP]: data?.backend_count || 0,
          [ERP_BILLS]: data?.epr_count || 0,
        });
      },
      errorCallback: () => setTotal(null)
    });
  }

  const loadData = ({
    view,
    query = null,
    initial = false,
    updateRequired = true,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, tableData, updateCurrentPage
    });

    setQueries({ ...queries, [view]: viewQuery});

    const finishTableLoading = () => {
      setLoading(prevState => ({...prevState, [view]: false}));
      if (updateRequired) {
        setRequiredUpdate(prevState => ({...prevState, [view]: false}));
      }
    }

    setLoading(prevState => ({...prevState, [view]: true}));

    getInvoices({
      query: getQuery(viewQuery, view),
      successCallback: (response) => {
        response.results = response.results.map(formatInvoice);
        tabHelpers.handleStoreTableDataWithPrev({
          tableData, setTableData, view, response, initial, updateCurrentPage
        });
        finishTableLoading();
        onSuccess?.();
      },
      errorCallback: finishTableLoading
    });
  }

  const {
    handleOnPaginate,
    handleOnTableChange,
    handleOnFilter,
    handleRowSelect,
    onChangeTab,
    updateTab,
  } = useAccountingTabLogic({
    tabKeys,
    tableData,
    setTableData,
    queries,
    setQueries,
    requiredUpdate,
    loadData,
    setActiveTabKey,
    setSelectedRowKeys,
  });

  const handleOnTabChange = (key) => {
    onChangeTab(key);
    if (detailsWindowProps.open) setDetailsWindowProps(prevState => ({...prevState, open: false}));
  }

  const trans = (key, props) => t(`accounting:${key}`, props);

  const syncEntries = ({ ids, onSuccess }) => {
    accountingHelpers.handleSyncItems({
      ids, loadData, onSuccess, onSync: syncInvoices, setSyncButtonLoading, trans
    });
  };

  const handleSync = () => {
    const ids = selectedRowKeys[NOT_ON_ERP] || [];
    syncEntries({
      ids,
      onSuccess: () => {
        setSelectedRowKeys(prevState => ({ ...prevState, [NOT_ON_ERP]: [] }));
        loadData({
          view: NOT_ON_ERP,
          updateCurrentPage: true,
          onSuccess: () => {
            if (tableData[SYNCED].loaded) {
              loadData({ initial: true, view: SYNCED });
            }
            updateTab(SYNCED);
            loadTotals();
          }
        })
      }
    });
  };

  const handleClickOnSyncButton = () => {
    const actions = {
      [NOT_ON_ERP]: handleSync,
      [SYNCED]: handleResync,
    };

    actions[activeTabKey]?.(activeTabKey);
  }

  const handleResync = () => {
    syncEntries({
      ids: selectedRowKeys?.[SYNCED],
      onSuccess: () => setSelectedRowKeys(prevState => ({ ...prevState, [SYNCED]: [] }))
    });
  };

  useEffect(() => {
    loadData({
      initial: true,
      view: tabKeys[activeTabKey.toUpperCase()]
    });
    loadTotals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatInvoice = (invoice) => accountingHelpers.formatInvoice({ invoice, vendors, employees });

  const getTableFiltersRightSideContent = (view) => {
    if (view === ERP_BILLS) return ;

    const availableSync = Boolean(selectedRowKeys?.[view].length);

    return (
      <TableFiltersRightSideContent
        syncButtonProps={{ disabled: !availableSync, loading: syncButtonLoading, onClick: availableSync && handleClickOnSyncButton }}
      />
    )
  }

  const getLabel = ({ key, totalKey }) =>
    tabHelpers.getLabelWithOptionalTotal({ key, total, totalKey, t: (key) => trans(`bills.${key}`) });

  const getQuery = (query, view) => {
    let statusesEnum = {
      [NOT_ON_ERP]: { source: billInvoiceSourceConstants.BACKEND, sync_status: billInvoiceSyncStatusConstants.NONE },
      [SYNCED]: { source: undefined, sync_status: billInvoiceSyncStatusConstants.ALL },
      [ERP_BILLS]: { source: billInvoiceSourceConstants.EPR, sync_status: undefined },
    };
    return {
      order_by: '-invoice_date',
      ...(statusesEnum[view] || {}),
      ...query,
    }
  }

  const handleRowUpdate = ({id, data, view, sendRequest = true}) => {
    if (sendRequest) {
      updateInvoice({
        id,
        data,
        successCallback: (invoice) => {
          const updatedData = accountingHelpers.formatInvoice({invoice});
          const updatedRows = tableData[view].rows.map(row => row.id === id ? updatedData : row);
          setTableData(prevState => ({
            ...prevState,
            [view]: {
              ...tableData[view],
              rows: updatedRows
            }
          }));
        },
        errorCallback: () => {
          showAlert('error', trans('validation.failedToUpdateJournalEntry'));
        }
      })
    } else {
      const updatedRows = tableData[view].rows.map(row => row.id === id ? {...row, ...data} : row);
      setTableData(prevState => ({
        ...prevState,
        [view]: {
          ...tableData[view],
          rows: updatedRows
        }
      }));
    }
  }

  const handleCloseDeleteInvoiceModal = () => {
    setDeleteModalProps(prevState => ({...prevState, open: false, loading: false}));
  }

  const handleOkDeleteInvoiceModal = () => {
    const id = deleteModalProps?.id;
    if (!id) return;

    setDeleteModalProps(prevState => ({...prevState, loading: true}));

    deleteInvoice({
      id,
      successCallback: () => {
        loadData({ view: activeTabKey, updateCurrentPage: true });

        showAlert('success', t('invoices:successInvoiceDeleteMessage'));
        handleCloseDeleteInvoiceModal();
        loadTotals();
      },
      errorCallback: () => {
        setDeleteModalProps(prevState => ({...prevState, loading: false}));
      }
    });
  }

  const handleRowDelete = (e, id) => {
    e.stopPropagation();

    if (!id) return;
    setDeleteModalProps(prevState => ({...prevState, open: true, id}));
  }

  const handleRowDownload = (e, id) => {
    e.stopPropagation();

    if (!id) return;
    downloadAccountingInvoice({
      id,
      successCallback: fileHelpers.saveFile,
      errorCallback: () => showAlert('error', t('validation.failedToDownloadFile')),
    });
  }

  const handleCloseDetails = () => {
    setDetailsWindowProps(prevState => ({ ...prevState, open: false }));
  }

  const handleMatchingVendorSuccess = () => {
    setDetailsWindowProps(prevState => ({ ...prevState, item: null, open: false }));
  }

  const handleSuccessDetails = (props) => {
    const actions = {
      [accountingEditVariants.VENDOR_MISSING]: handleMatchingVendorSuccess,
    };
    actions[detailsWindowProps.variant]?.(props);
  }

  const handleClickOnNotOnERP = (e, record) => {
    if (elementHelpers.isInputOrButtonElement(e)) {
      e.stopPropagation();
      return;
    }

    if (record.vendor && !vendorsHelpers.isVendorAccountingReference(record.vendor)) {
      setDetailsWindowProps(prevState => ({ ...prevState, open: true, item: record }));
    }
  }

  const handleRefresh = () => loadData({ initial: true, view: tabKeys[activeTabKey.toUpperCase()] });

  const getTabProps = (key) => {
    let tableProps = {};
    let tableFiltersProps = {};
    if (key === SYNCED) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          onChange: (rows) => handleRowSelect(key, rows),
          selectedRowKeys: selectedRowKeys[key],
          syncStatus: billInvoiceSyncStatusConstants.ERROR
        })
      }
      tableFiltersProps = { leftSideContent: <ReloadButton onClick={handleRefresh} /> };
    } else if (key === NOT_ON_ERP) {
      tableProps = {
        ...tableProps,
        rowSelection: accountingHelpers.getTableRowSelection({
          onChange: (rows) => handleRowSelect(key, rows),
          selectedRowKeys: selectedRowKeys[key],
        }),
        onRow: (record) => ({
          onClick: (e) => handleClickOnNotOnERP(e, record),
        })
      }
    }

    return accountingHelpers.getInvoicesTabProps({
      key,
      totalKey: key,
      tableData,
      loading,
      handleOnFilter,
      handleRowUpdate,
      handleOnTableChange,
      handleOnPaginate,
      getTableFiltersRightSideContent,
      total,
      tableFiltersProps,
      tableProps,
      employees,
      vendors,
    });
  }

  const tabs = [
    {
      key: NOT_ON_ERP,
      label: getLabel({ key: NOT_ON_ERP, totalKey: NOT_ON_ERP }),
      children: (
        <MissingErpTab
          {...getTabProps(NOT_ON_ERP)}
          onRowDelete={handleRowDelete}
          onRowDownload={handleRowDownload}
        />
      )
    },
    {
      key: SYNCED,
      label: getLabel({ key: SYNCED }),
      children: <BillsHistoryTab {...getTabProps(SYNCED)} />
    },
    {
      key: ERP_BILLS,
      label: getLabel({ key: ERP_BILLS, totalKey: ERP_BILLS }),
      children: <ErpBillsTab {...getTabProps(ERP_BILLS)} />
    },
  ];

  return (
    <StyledJournalEntriesTabsContainer>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <StyledBillsTabs
            style={{ ...tableStyles.default, ...tableStyles[state] }}
          >
            <Tabs
              {...rest}
              activeKey={activeTabKey}
              items={tabs}
              onChange={handleOnTabChange}
              type='card'
            />
          </StyledBillsTabs>
        )}
      </Transition>

      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <EditAccountingRowSection
            {...detailsWindowProps}
            className='accounting-details-panel'
            contentClassName='accounting-details-content'
            onBack={handleCloseDetails}
            onSuccess={handleSuccessDetails}
            style={{ ...detailsStyles.default, ...detailsStyles[state] }}
          />
        )}
      </Transition>

      <StyledBillsTabsDeleteModal
        description={trans('modal.invoiceDelete.description')}
        onCancel={handleCloseDeleteInvoiceModal}
        onOk={handleOkDeleteInvoiceModal}
        title={trans('modal.invoiceDelete.title')}
        {...deleteModalProps}
      />
    </StyledJournalEntriesTabsContainer>
  );
}

const mapStateToProps = ({
  company: { employees },
  vendor: { vendors },
}) => ({
  employees,
  vendors,
});

const mapDispatchToProps = {
  deleteInvoice: accountingActions.deleteInvoice,
  downloadAccountingInvoice: invoicesActions.downloadAccountingInvoice,
  getBatchVendors: vendorsActions.getBatchVendors,
  getInvoices: accountingActions.getInvoices,
  getInvoicesTotals: accountingActions.getInvoicesTotals,
  syncInvoices: accountingActions.syncInvoices,
  updateInvoice: accountingActions.updateInvoice,
}

export default connect(mapStateToProps, mapDispatchToProps)(BillsTabs);
